import React, { ReactNode, useCallback, useState } from "react";

import { Tree } from "antd";
import { DataNode, EventDataNode } from "antd/es/tree";
import { useIntl } from "react-intl";
import AutoSizer from "react-virtualized-auto-sizer";

import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import LookupData from "@mapmycustomers/shared/types/territory/LookupData";
import { LoadingSpinner, SearchField } from "@mapmycustomers/ui";

import { getAreaTypeCaption } from "@app/util/ui";

import styles from "./AreaRegionList.module.scss";
import useTreeData from "./useTreeData";

interface Props {
  action?: ReactNode;
  areaType: AreaType;
  loading?: boolean;
  lookupData: LookupData;
  onSelectionChange?: (selectedItems: AreaRegion["id"][]) => void;
  selectedRegionIds?: AreaRegion["id"][];
}

const AreaRegionList: React.FC<Props> = ({
  action,
  areaType,
  loading,
  lookupData,
  onSelectionChange,
  selectedRegionIds,
}) => {
  const intl = useIntl();

  const [filter, setFilter] = useState("");

  const [treeData, expandedKeys, treeDataLoading, checkedTreeKeys, handleTreeCheck, handleExpand] =
    useTreeData(areaType, lookupData, filter, selectedRegionIds, onSelectionChange);

  // highlight nodes which match the search
  const handleTreeHighlights = useCallback(
    (node: EventDataNode<DataNode>) =>
      (node.title as string).toLowerCase().includes(filter.toLowerCase()),
    [filter]
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{getAreaTypeCaption(intl, areaType)}</div>
        <div className={styles.searchFieldContainer}>
          <SearchField
            allowClear
            className={styles.searchField}
            onChange={setFilter}
            placeholder={intl.formatMessage({
              id: "map.territories.sidebar.createEdit.smart.items.search.placeholder",
              defaultMessage: "Search here",
              description:
                "Search field for the list of regions in the Create Smart Territory sidebar",
            })}
            size="middle"
          />
          {action}
        </div>
      </div>

      <div className={styles.list}>
        {loading || treeDataLoading ? (
          <LoadingSpinner />
        ) : (
          <AutoSizer>
            {({ height }: { height: number }) => (
              <Tree
                checkable
                checkedKeys={checkedTreeKeys}
                checkStrictly
                className={styles.tree}
                expandedKeys={expandedKeys}
                filterTreeNode={handleTreeHighlights}
                height={height}
                onCheck={handleTreeCheck}
                onExpand={handleExpand}
                selectable={false}
                treeData={treeData}
                virtual
              />
            )}
          </AutoSizer>
        )}
      </div>
    </div>
  );
};

export default AreaRegionList;
